import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {McpInstallButton, type McpInstallTarget} from '@github-ui/mcp-install-button'
import {TerminalIcon, TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Link} from '@primer/react'
import {useId, useState} from 'react'

import styles from './AIResourceResults.module.css'

export type AIResourceInstallAction =
  | {type: 'mcp_server'; target: McpInstallTarget}
  | {type: 'skill'; command: string; hasCompanionFiles: boolean}

export type AIResourceResult = {
  id: string
  /** An authorized canonical destination supplied by the composing application. */
  href: string
  name: string
  description?: string
  resourceType: string
  resourceTypeIcon?: Icon
  publisher?: {
    name: string
    /** An authorized canonical destination supplied by the composing application. */
    href: string
  }
  labels: readonly string[]
  installAction?: AIResourceInstallAction
}

export type AIResourceResultsProps = {
  results: readonly AIResourceResult[]
}

export function AIResourceResults({results}: AIResourceResultsProps) {
  if (results.length === 0) return null

  return (
    <ListView itemsListClassName={styles.List} title="AI resources" totalCount={results.length}>
      {results.map(result => (
        <AIResourceResultRow key={result.id} result={result} />
      ))}
    </ListView>
  )
}

export function AIResourceResultRow({result}: {result: AIResourceResult}) {
  const ResourceTypeIcon = result.resourceTypeIcon

  return (
    <ListItem
      className={styles.Row}
      title={<ListItemTitle href={result.href} value={result.name} linkProps={{as: Link, className: styles.Link}} />}
      metadata={result.installAction ? <InstallAction action={result.installAction} name={result.name} /> : undefined}
      metadataContainerClassName={styles.InstallMetadata}
    >
      <ListItemMainContent>
        <div className={styles.Content}>
          {result.description && (
            <ListItemDescription>
              <ListItemDescriptionItem>{result.description}</ListItemDescriptionItem>
            </ListItemDescription>
          )}

          <ListItemDescription>
            <span className={styles.Metadata}>
              <span className={styles.ResourceType}>
                {ResourceTypeIcon && <ResourceTypeIcon aria-hidden="true" data-testid="resource-type-icon" />}
                <span>{result.resourceType}</span>
              </span>
              {result.publisher && (
                <>
                  <span className={styles.Separator} aria-hidden="true" />
                  <Link className={styles.Link} href={result.publisher.href}>
                    {result.publisher.name}
                  </Link>
                </>
              )}
            </span>
          </ListItemDescription>
        </div>
      </ListItemMainContent>
    </ListItem>
  )
}

function InstallAction({action, name}: {action: AIResourceInstallAction; name: string}) {
  if (action.type === 'mcp_server') {
    return (
      <McpInstallButton
        mcp={action.target}
        muted
        ariaLabel={`Install ${name}`}
        analyticsResourceName={name}
        copilotAppEntryPoint="dotcom_ai-resource-search_install"
      />
    )
  }

  return <SkillInstallAction action={action} name={name} />
}

function SkillInstallAction({action, name}: {action: Extract<AIResourceInstallAction, {type: 'skill'}>; name: string}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <div className={styles.SkillInstallMenu}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <TerminalIcon aria-hidden="true" data-testid="skill-install-terminal-icon" />
          <span>Copilot CLI</span>
        </h4>
        <SkillInstallOption
          title={action.hasCompanionFiles ? 'Install skill with supporting files' : 'Install skill'}
          description={
            action.hasCompanionFiles
              ? 'Clone the repository to include the supporting files this skill requires. Keep the clone in place.'
              : 'This skill doesn’t require any supporting files.'
          }
          command={action.command}
          multiline={action.hasCompanionFiles}
        />
      </div>
    </AnchoredOverlay>
  )
}

function SkillInstallOption({
  title,
  description,
  command,
  multiline = false,
}: {
  title: string
  description: string
  command: string
  multiline?: boolean
}) {
  return (
    <section className={styles.SkillInstallOption}>
      <h5 className={styles.SkillInstallOptionHeading}>{title}</h5>
      <p className={styles.SkillInstallDescription}>{description}</p>
      <div className={styles.SkillInstallCommand}>
        <pre
          aria-label={`${title} command`}
          className={`${styles.SkillInstallCode} ${multiline ? styles.SkillInstallMultilineCode : ''}`}
          role="region"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard-scrollable overflow region
          tabIndex={0}
        >
          <code>{command}</code>
        </pre>
        <CopyToClipboardIconButton
          className={styles.SkillInstallCopyButton}
          textToCopy={command}
          ariaLabel="Copy command to clipboard"
          tooltipProps={{direction: 'nw'}}
        />
      </div>
    </section>
  )
}
